export enum SolicitacaoDocumentoTipo {
  ATO_APOSENTADORIA = 'ato_aposentadoria',
  CERTIDAO_TEMPO_CONTRIBUICAO = 'certidao_tempo_contribuicao',
  CERTIDAO_OU_DECLARACAO_FUNCIONAL = 'certidao_ou_declaracao_funcional',
  OUTROS_DOCUMENTOS = 'outros_documentos'
}

export enum SolicitacaoDocumentoEspecifico {
  CERTIDAO = 'certidao_funcional',
  DECLARACAO_FUNCIONAL = 'declaracao_funcional'
}

export enum SolicitacaoDocumentoFinalidade {
  CONCURSO_PUBLICO = 'concurso_publico',
  AVERBACAO_NO_ORGAO = 'averbacao_no_orgao',
  ACAO_JUDICIAL = 'acao_judicial',
  COMPROVACAO_INSS = 'comprovacao_inss',
  OUTROS = 'outros'
}

export enum SolicitacaoDocumentoField {
  DOCUMENTO_ESPECIFICO = 'documentoEspecifico',
  NOME_COMPLETO = 'nomeCompleto',
  CPF = 'cpf',
  MATRICULA = 'matricula',
  TELEFONE = 'telefone',
  EMAIL = 'email',
  EMAIL_RESPOSTA = 'emailResposta',
  ORGAO_DESTINO = 'orgaoDestino',
  FINALIDADE = 'finalidade',
  DESTINO = 'destino',
  OBJETO = 'objeto',
  AGENDAMENTO = 'agendamento',
  ESPECIFICAR = 'especificar',
  DESCRICAO_DETALHADA = 'descricaoDetalhada',
  ARQUIVO = 'arquivo'
}

export interface SolicitacaoDocumentoOption<T = string> {
  value: T;
  nome: string;
}

export interface SolicitacaoDocumentoFormValue {
  tipoDocumentoDesejado: SolicitacaoDocumentoTipo | '';
  documentoEspecifico: SolicitacaoDocumentoEspecifico | '';
  nomeCompleto: string;
  cpf: string;
  matricula: string;
  telefone: string;
  email: string;
  emailResposta: string;
  orgaoDestino: string;
  finalidade: SolicitacaoDocumentoFinalidade | '';
  destino: string;
  objeto: string;
  agendamento: string;
  especificar: string;
  descricaoDetalhada: string;
}

type SolicitacaoDocumentoPayloadValue = string | number;

export interface SolicitacaoDocumentoContext {
  visibleFields: SolicitacaoDocumentoField[];
  requiredFields: SolicitacaoDocumentoField[];
  additionalField?: SolicitacaoDocumentoField;
  attachmentRequired: boolean;
  documentoEspecificoOptions: SolicitacaoDocumentoOption<SolicitacaoDocumentoEspecifico>[];
  finalidadeOptions: SolicitacaoDocumentoOption<SolicitacaoDocumentoFinalidade>[];
}

interface SolicitacaoDocumentoTypeConfig {
  visibleFields: SolicitacaoDocumentoField[];
  requiredFields: SolicitacaoDocumentoField[];
  attachmentRequired: boolean;
  documentoEspecificoOptions?: SolicitacaoDocumentoOption<SolicitacaoDocumentoEspecifico>[];
  finalidadeOptions?: SolicitacaoDocumentoOption<SolicitacaoDocumentoFinalidade>[];
  finalidadeExtraFields?: Partial<Record<SolicitacaoDocumentoFinalidade, SolicitacaoDocumentoField>>;
}

const EMPTY_CONTEXT: SolicitacaoDocumentoContext = {
  visibleFields: [],
  requiredFields: [],
  attachmentRequired: false,
  documentoEspecificoOptions: [],
  finalidadeOptions: []
};

export const SOLICITACAO_DOCUMENTO_TIPO_OPTIONS: SolicitacaoDocumentoOption<SolicitacaoDocumentoTipo>[] = [
  { value: SolicitacaoDocumentoTipo.ATO_APOSENTADORIA, nome: 'Ato de Aposentadoria' },
  { value: SolicitacaoDocumentoTipo.CERTIDAO_TEMPO_CONTRIBUICAO, nome: 'Certidão de Tempo de Contribuição' },
  { value: SolicitacaoDocumentoTipo.CERTIDAO_OU_DECLARACAO_FUNCIONAL, nome: 'Certidão ou Declaração Funcional' },
  { value: SolicitacaoDocumentoTipo.OUTROS_DOCUMENTOS, nome: 'Outros documentos' }
];

const DOCUMENTO_ESPECIFICO_OPTIONS: SolicitacaoDocumentoOption<SolicitacaoDocumentoEspecifico>[] = [
  { value: SolicitacaoDocumentoEspecifico.CERTIDAO, nome: 'Certidão' },
  { value: SolicitacaoDocumentoEspecifico.DECLARACAO_FUNCIONAL, nome: 'Declaração Funcional' }
];

const FINALIDADE_OPTIONS: SolicitacaoDocumentoOption<SolicitacaoDocumentoFinalidade>[] = [
  { value: SolicitacaoDocumentoFinalidade.CONCURSO_PUBLICO, nome: 'Concurso Público' },
  { value: SolicitacaoDocumentoFinalidade.AVERBACAO_NO_ORGAO, nome: 'Averbação no órgão' },
  { value: SolicitacaoDocumentoFinalidade.ACAO_JUDICIAL, nome: 'Ação Judicial' },
  { value: SolicitacaoDocumentoFinalidade.COMPROVACAO_INSS, nome: 'Comprovação INSS' },
  { value: SolicitacaoDocumentoFinalidade.OUTROS, nome: 'Outros' }
];

const TIPO_DOCUMENTO_CODE_MAP: Record<SolicitacaoDocumentoTipo, number> = {
  [SolicitacaoDocumentoTipo.ATO_APOSENTADORIA]: 1,
  [SolicitacaoDocumentoTipo.CERTIDAO_TEMPO_CONTRIBUICAO]: 2,
  [SolicitacaoDocumentoTipo.CERTIDAO_OU_DECLARACAO_FUNCIONAL]: 3,
  [SolicitacaoDocumentoTipo.OUTROS_DOCUMENTOS]: 4
};

const DOCUMENTO_ESPECIFICO_CODE_MAP: Record<SolicitacaoDocumentoEspecifico, number> = {
  [SolicitacaoDocumentoEspecifico.CERTIDAO]: 1,
  [SolicitacaoDocumentoEspecifico.DECLARACAO_FUNCIONAL]: 2
};

const FINALIDADE_CODE_MAP: Record<SolicitacaoDocumentoFinalidade, number> = {
  [SolicitacaoDocumentoFinalidade.CONCURSO_PUBLICO]: 1,
  [SolicitacaoDocumentoFinalidade.AVERBACAO_NO_ORGAO]: 2,
  [SolicitacaoDocumentoFinalidade.ACAO_JUDICIAL]: 3,
  [SolicitacaoDocumentoFinalidade.COMPROVACAO_INSS]: 4,
  [SolicitacaoDocumentoFinalidade.OUTROS]: 5
};

const TIPO_DOCUMENTO_REVERSE_MAP = invertNumberMap(TIPO_DOCUMENTO_CODE_MAP);
const DOCUMENTO_ESPECIFICO_REVERSE_MAP = invertNumberMap(DOCUMENTO_ESPECIFICO_CODE_MAP);
const FINALIDADE_REVERSE_MAP = invertNumberMap(FINALIDADE_CODE_MAP);

const TIPO_DOCUMENTO_CONFIG: Record<SolicitacaoDocumentoTipo, SolicitacaoDocumentoTypeConfig> = {
  [SolicitacaoDocumentoTipo.ATO_APOSENTADORIA]: {
    visibleFields: [
      SolicitacaoDocumentoField.NOME_COMPLETO,
      SolicitacaoDocumentoField.CPF,
      SolicitacaoDocumentoField.TELEFONE,
      SolicitacaoDocumentoField.EMAIL
    ],
    requiredFields: [
      SolicitacaoDocumentoField.NOME_COMPLETO,
      SolicitacaoDocumentoField.CPF,
      SolicitacaoDocumentoField.TELEFONE,
      SolicitacaoDocumentoField.EMAIL,
      SolicitacaoDocumentoField.ARQUIVO
    ],
    attachmentRequired: true
  },
  [SolicitacaoDocumentoTipo.CERTIDAO_TEMPO_CONTRIBUICAO]: {
    visibleFields: [
      SolicitacaoDocumentoField.ORGAO_DESTINO,
      SolicitacaoDocumentoField.EMAIL_RESPOSTA
    ],
    requiredFields: [
      SolicitacaoDocumentoField.ORGAO_DESTINO,
      SolicitacaoDocumentoField.EMAIL_RESPOSTA
    ],
    attachmentRequired: false
  },
  [SolicitacaoDocumentoTipo.CERTIDAO_OU_DECLARACAO_FUNCIONAL]: {
    visibleFields: [
      SolicitacaoDocumentoField.DOCUMENTO_ESPECIFICO,
      SolicitacaoDocumentoField.NOME_COMPLETO,
      SolicitacaoDocumentoField.MATRICULA,
      SolicitacaoDocumentoField.CPF,
      SolicitacaoDocumentoField.TELEFONE,
      SolicitacaoDocumentoField.EMAIL_RESPOSTA,
      SolicitacaoDocumentoField.FINALIDADE
    ],
    requiredFields: [
      SolicitacaoDocumentoField.DOCUMENTO_ESPECIFICO,
      SolicitacaoDocumentoField.NOME_COMPLETO,
      SolicitacaoDocumentoField.MATRICULA,
      SolicitacaoDocumentoField.CPF,
      SolicitacaoDocumentoField.TELEFONE,
      SolicitacaoDocumentoField.EMAIL_RESPOSTA,
      SolicitacaoDocumentoField.FINALIDADE
    ],
    attachmentRequired: false,
    documentoEspecificoOptions: DOCUMENTO_ESPECIFICO_OPTIONS,
    finalidadeOptions: FINALIDADE_OPTIONS,
    finalidadeExtraFields: {
      [SolicitacaoDocumentoFinalidade.CONCURSO_PUBLICO]: SolicitacaoDocumentoField.ORGAO_DESTINO,
      [SolicitacaoDocumentoFinalidade.AVERBACAO_NO_ORGAO]: SolicitacaoDocumentoField.DESTINO,
      [SolicitacaoDocumentoFinalidade.ACAO_JUDICIAL]: SolicitacaoDocumentoField.OBJETO,
      [SolicitacaoDocumentoFinalidade.COMPROVACAO_INSS]: SolicitacaoDocumentoField.AGENDAMENTO,
      [SolicitacaoDocumentoFinalidade.OUTROS]: SolicitacaoDocumentoField.ESPECIFICAR
    }
  },
  [SolicitacaoDocumentoTipo.OUTROS_DOCUMENTOS]: {
    visibleFields: [SolicitacaoDocumentoField.DESCRICAO_DETALHADA],
    requiredFields: [SolicitacaoDocumentoField.DESCRICAO_DETALHADA],
    attachmentRequired: false
  }
};

export const SOLICITACAO_DOCUMENTO_DYNAMIC_FIELDS: SolicitacaoDocumentoField[] = [
  SolicitacaoDocumentoField.DOCUMENTO_ESPECIFICO,
  SolicitacaoDocumentoField.NOME_COMPLETO,
  SolicitacaoDocumentoField.CPF,
  SolicitacaoDocumentoField.MATRICULA,
  SolicitacaoDocumentoField.TELEFONE,
  SolicitacaoDocumentoField.EMAIL,
  SolicitacaoDocumentoField.EMAIL_RESPOSTA,
  SolicitacaoDocumentoField.ORGAO_DESTINO,
  SolicitacaoDocumentoField.FINALIDADE,
  SolicitacaoDocumentoField.DESTINO,
  SolicitacaoDocumentoField.OBJETO,
  SolicitacaoDocumentoField.AGENDAMENTO,
  SolicitacaoDocumentoField.ESPECIFICAR,
  SolicitacaoDocumentoField.DESCRICAO_DETALHADA
];

export function getSolicitacaoDocumentoContext(
  value: Partial<SolicitacaoDocumentoFormValue> | null | undefined
): SolicitacaoDocumentoContext {
  if (!value || !value.tipoDocumentoDesejado) {
    return EMPTY_CONTEXT;
  }

  const tipoDocumento = normalizeSolicitacaoDocumentoTipo(value.tipoDocumentoDesejado);
  const config = tipoDocumento ? TIPO_DOCUMENTO_CONFIG[tipoDocumento] : undefined;

  if (!config) {
    return EMPTY_CONTEXT;
  }

  const visibleFields = config.visibleFields.slice();
  const requiredFields = config.requiredFields.slice();
  const finalidade = normalizeSolicitacaoDocumentoFinalidade(value.finalidade);
  const additionalField = config.finalidadeExtraFields && finalidade
    ? config.finalidadeExtraFields[finalidade]
    : undefined;

  if (additionalField) {
    visibleFields.push(additionalField);
    requiredFields.push(additionalField);
  }

  return {
    visibleFields,
    requiredFields,
    additionalField,
    attachmentRequired: config.attachmentRequired,
    documentoEspecificoOptions: config.documentoEspecificoOptions || [],
    finalidadeOptions: config.finalidadeOptions || []
  };
}

export function buildSolicitacaoDocumentoPayload(
  value: Partial<SolicitacaoDocumentoFormValue> | null | undefined
): { [key: string]: SolicitacaoDocumentoPayloadValue } {
  if (!value || !value.tipoDocumentoDesejado) {
    return {};
  }

  const context = getSolicitacaoDocumentoContext(value);
  const tipoDocumento = normalizeSolicitacaoDocumentoTipo(value.tipoDocumentoDesejado);

  if (!tipoDocumento) {
    return {};
  }

  const payload: { [key: string]: SolicitacaoDocumentoPayloadValue } = {
    tipoDocumentoDesejado: TIPO_DOCUMENTO_CODE_MAP[tipoDocumento]
  };

  context.visibleFields.forEach(field => {
    const fieldValue = normalizePayloadFieldValue(field, value[field]);

    if (fieldValue !== undefined) {
      payload[field] = fieldValue;
    }
  });

  return payload;
}

export function normalizeSolicitacaoDocumentoFormValue(
  value: any
): Partial<SolicitacaoDocumentoFormValue> {
  if (!value) {
    return {};
  }

  return {
    ...value,
    tipoDocumentoDesejado: normalizeSolicitacaoDocumentoTipo(value.tipoDocumentoDesejado) || '',
    documentoEspecifico: normalizeSolicitacaoDocumentoEspecifico(value.documentoEspecifico) || '',
    finalidade: normalizeSolicitacaoDocumentoFinalidade(value.finalidade) || ''
  };
}

function normalizePayloadValue(value: unknown): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }

  if (typeof value !== 'string') {
    return String(value);
  }

  const normalized = value.trim();
  return normalized ? normalized : undefined;
}

function normalizePayloadFieldValue(
  field: SolicitacaoDocumentoField,
  value: unknown
): SolicitacaoDocumentoPayloadValue | undefined {
  switch (field) {
    case SolicitacaoDocumentoField.DOCUMENTO_ESPECIFICO: {
      const documentoEspecifico = normalizeSolicitacaoDocumentoEspecifico(value);
      return documentoEspecifico ? DOCUMENTO_ESPECIFICO_CODE_MAP[documentoEspecifico] : undefined;
    }
    case SolicitacaoDocumentoField.FINALIDADE: {
      const finalidade = normalizeSolicitacaoDocumentoFinalidade(value);
      return finalidade ? FINALIDADE_CODE_MAP[finalidade] : undefined;
    }
    default:
      return normalizePayloadValue(value);
  }
}

function normalizeSolicitacaoDocumentoTipo(
  value: unknown
): SolicitacaoDocumentoTipo | undefined {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'number') {
    return TIPO_DOCUMENTO_REVERSE_MAP[value];
  }

  return TIPO_DOCUMENTO_CONFIG[value as SolicitacaoDocumentoTipo]
    ? value as SolicitacaoDocumentoTipo
    : TIPO_DOCUMENTO_REVERSE_MAP[Number(value)];
}

function normalizeSolicitacaoDocumentoEspecifico(
  value: unknown
): SolicitacaoDocumentoEspecifico | undefined {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'number') {
    return DOCUMENTO_ESPECIFICO_REVERSE_MAP[value];
  }

  return DOCUMENTO_ESPECIFICO_CODE_MAP[value as SolicitacaoDocumentoEspecifico]
    ? value as SolicitacaoDocumentoEspecifico
    : DOCUMENTO_ESPECIFICO_REVERSE_MAP[Number(value)];
}

function normalizeSolicitacaoDocumentoFinalidade(
  value: unknown
): SolicitacaoDocumentoFinalidade | undefined {
  if (!value) {
    return undefined;
  }

  if (typeof value === 'number') {
    return FINALIDADE_REVERSE_MAP[value];
  }

  return FINALIDADE_CODE_MAP[value as SolicitacaoDocumentoFinalidade]
    ? value as SolicitacaoDocumentoFinalidade
    : FINALIDADE_REVERSE_MAP[Number(value)];
}

function invertNumberMap<T extends string>(source: Record<T, number>): Record<number, T> {
  return Object.keys(source).reduce((acc, key) => {
    const typedKey = key as T;
    acc[source[typedKey]] = typedKey;
    return acc;
  }, {} as Record<number, T>);
}
